import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import GoogleMapReact from "google-map-react";
import { makeStyles, Theme, useTheme } from "@material-ui/core/styles";
import { useDispatch, useSelector } from "react-redux";
import {
  EventCard,
  Fab,
  LightColors,
  MarkerCluster,
  Menu,
  MobileMenu,
  Switch,
  Tooltip,
  Typography,
  WebMenuItem,
} from "@thingsw/pitta-design-system";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import clsx from "clsx";

import CheckIcon from "@material-ui/icons/Check";

import { TopRightControl } from "./TopRightControl";
import {
  convertGeofenceToGeometries,
  getLocationLabel,
  renderGeofences,
  toggleFullscreen,
} from "../../utils/GoogleMap";
import { RootState } from "../../features/store";
import {
  GPSMessage,
  IGPSLocation,
  IInfoWindow,
  ILatLngBounds,
} from "../../types";

import GpsFixedIcon from "@material-ui/icons/GpsFixed";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";
import { Webviewer } from "../../contants/Breakpoints";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { PUSH_EVENT } from "../../features/PushEvent/slice";
import {
  CAMERA,
  ICameraInfo,
  IPublicCamera,
} from "../../features/Camera/slice";
import { INITIAL_MAP_LOCATION } from "./GoogleMap";
import { IGeofence } from "../../features/Geofence/slice";
import { GOOGLE_API_KEY } from "../../contants/GoogleMap";
import { loadUserSettings, USER } from "../../features/User/slice";
import { detect } from "detect-browser";
import { setFullscreen as setFullscreenTheme } from "../../features/Theme/slice";
import { LOCATION_WS } from "../../features/LocationWS/slice";

const LocationWidth = 64;
const LocationHeight = 73;

const useStyles = makeStyles((theme: Theme) => ({
  rootFullscreen: {
    position: "fixed",
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    display: "flex",
    zIndex: 9999,
    backgroundColor: "black",
  },
  topControlPane: {
    position: "absolute",
    top: 0,
    ...(theme.direction === "rtl" ? { left: 0 } : { right: 0 }),
    padding: theme.spacing(2),
    display: "flex",
    overflow: "visible",
  },
  zoomControlDiv: {
    position: "absolute",
    bottom: 0,
    ...(theme.direction === "rtl" ? { left: 0 } : { right: 0 }),
    padding: theme.spacing(2),
    display: "flex",
    overflow: "visible",
    flexDirection: "column",
  },
  zoomControlBtn: {
    borderRadius: 8,
    width: 30,
    height: 30,
    color: LightColors.primary["3"],
    minHeight: 30,
  },
  gpsFixBtn: {
    marginBottom: theme.spacing(0.5),
  },
  gpsFixBtnTouch: {
    color: `${LightColors.primary["7"]}!important`,
  },
  noHover: {
    "&:hover": {
      fill: LightColors.primary["3"],
      color: LightColors.primary["3"],
    },
  },
  zoomInBtn: {
    borderBottom: 0,
    borderRadius: theme.spacing(1, 1, 0, 0),
    boxShadow: "none",
  },
  zoomOutBtn: {
    borderTop: 0,
    borderRadius: theme.spacing(0, 0, 1, 1),
    boxShadow: "none",
  },
  tnpDiv: {
    ...(theme.direction === "rtl" ? { paddingRight: 44 } : { paddingLeft: 44 }),
  },
  appIcon: {
    fontSize: 15,
    color: LightColors.primary["1"],
    ...(theme.direction === "rtl"
      ? { marginLeft: theme.spacing(1) }
      : { marginRight: theme.spacing(1) }),
  },
  mobileMemuItem: {
    padding: theme.spacing(1.5, 2),
  },
  directionMakerIcon: {
    width: 20,
    height: 20,
    zIndex: 1,
  },
  markerIcon: {
    cursor: "pointer",
    transform: "translate(-17px, -32px)",
    width: 34,
    height: 38,
    zIndex: 2,
  },
  selectedMarkerIcon: {
    cursor: "pointer",
    transform: "translate(-19px, -38px)",
    width: 38,
    height: 42,
  },
  startMarkerIcon: {
    zIndex: 2,
    transform: "translate(-12px, -12px)",
    filter:
      "drop-shadow(0px 0px 1px rgba(0, 0, 0, 0.14)), drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.12)), drop-shadow(0px 0px 3px rgba(0, 0, 0, 0.2))",
  },
  infoWindowDiv: {
    width: 300,
    backgroundColor: LightColors.primary["0"],
    borderRadius: 4,
    border: `1px sloid ${LightColors.primary["6"]}`,
  },
  privateCamDiv: {
    width: 120,
    padding: theme.spacing(1),
    backgroundColor: LightColors.primary["0"],
    borderRadius: 4,
    border: `1px sloid ${LightColors.primary["6"]}`,
    display: "flex",
    alignItems: "center",
  },
  privateCamWindowCenter: {
    transform: "translate(-70px, calc(-100% - 20px))",
  },
  parkingDiv: {
    maxHeight: 421,
    boxShadow:
      "0px 6px 20px rgba(0, 0, 0, 0.05), 0px 3px 15px rgba(0, 0, 0, 0.1), 0px 0px 8px rgba(0, 0, 0, 0.08)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  infoWindowCenter: {
    transform: "translate(-150px, calc(-100% - 40px))",
  },
  inforParkingTitle: {
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${LightColors.primary["6"]}`,
  },
  textTransform: {
    textTransform: "none",
  },
  locationLabel: {
    backgroundColor: "white",
  },
  camFilterDiv: {
    minWidth: 311,
    padding: "0 16px",
    // mantis - 10778, 모바일 피그마 적용 및 잘려보이는 것 방지하기 위한 사이즈 조정 (Leehj)
    [theme.breakpoints.down(430)]: {
      minWidth: 343,
    },
    [theme.breakpoints.down(360)]: {
      minWidth: 311,
    },
    [theme.breakpoints.down(330)]: {
      minWidth: 290,
    },
  },
  camFilterTitle: {
    margignBottom: 6.5,
    paddingBottom: 4,
    borderBottom: `2px solid ${LightColors.primary["9"]}`,
  },
  camFilterListItem: {
    display: "flex",
    height: 48,
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 12,
    paddingRight: 5,
  },
  camFilterRoot: {
    paddingTop: 15,
    paddingBottom: 7,
  },
}));

interface LocationMapProps {
  onGoogleApiLoaded?: (maps: {
    map: any;
    maps: any;
    ref: Element | null;
  }) => void;
  publicIcon?: boolean;
  fullscreenIcon?: boolean;
  trackMarker?: boolean;
  location?: IGPSLocation;
  locations?: IGPSLocation[];
  children?: React.ReactNode;
  showHeading?: boolean;
  disableAutoZoom?: boolean;
  noMarker?: boolean;
  center?: { lat: number; lng: number; zoom?: number };
  mode?: "default" | "liveview";
  onSetInfoWindow?: (infoWindow?: IInfoWindow) => void;
  onUpdateBounds?: (bounds: ILatLngBounds) => void;
  publicCams?: IPublicCamera[];
  filteredCams?: ICameraInfo[];
  onLiveView?: (psn: string, isPublic: boolean) => void;
  geofences?: IGeofence[];
  mapRef?: React.RefObject<HTMLDivElement>;
  app?: boolean;
  showLocation?: boolean;
  isPublic?: boolean;
  speedUnit?: "0" | "1";
}

export const LocationMap = (props: LocationMapProps) => {
  const {
    onGoogleApiLoaded,
    publicIcon,
    fullscreenIcon,
    trackMarker,
    location,
    locations,
    children,
    showHeading,
    center,
    disableAutoZoom,
    noMarker,
    mode,
    publicCams,
    filteredCams,
    onSetInfoWindow,
    onUpdateBounds,
    onLiveView,
    geofences,
    mapRef,
    app,
    showLocation,
    isPublic,
    speedUnit,
  } = props;
  // useTraceUpdate(props);
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme() as Theme;
  const dispatch = useDispatch();
  const mobile = useMediaQuery(theme.breakpoints.down(Webviewer.mobile));
  // const [gpsWS, readyState] = useGpsWS();

  const publicRef = useRef<HTMLButtonElement>(null);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const mapDivRef = useRef<HTMLDivElement>(null);
  const initialRequest = useRef(true);

  const { clusters } = useSelector((state: RootState) => state[CAMERA]);
  const { webPushEvent } = useSelector((state: RootState) => state[PUSH_EVENT]);
  const { email, userSettings } = useSelector(
    (state: RootState) => state[USER]
  );
  const { gpsWS, gpsWsReadyState } = useSelector(
    (state: RootState) => state[LOCATION_WS]
  );

  const [map, setMap] = useState<any>();
  const [maps, setMaps] = useState<any>();
  const [openLayer, setOpenLayer] = useState(false);
  const [mapMode, setMapMode] = useState<"map" | "satellite">("map");
  const [markers, setMarkers] = useState<{ [key: string]: any }>({});
  // const [gMarkers, setGMarkers] = useState<any[]>([]);
  const [bounds, setBounds] = useState<ILatLngBounds>();
  const [selectedMarker, setSelectedMarker] = useState<any>();
  const [privateCamMarker, setPrivateCamMarker] = useState<any>();
  const [activePublic, setActivePublic] = useState(true);
  const [openCamFilter, setOpenCamFilter] = useState(false);

  const prevLocation = useRef<IGPSLocation>();
  const locationMarker = useRef<any>();
  const gMarkers = useRef<any[]>([]);
  const publicMarkers = useRef<any[]>([]);
  const prevBounds = useRef<any>();

  const [fullscreen, setfullscreen] = useState(false);
  const [initMap, setInitMap] = useState(false);

  const [touch, setTouch] = useState(false);
  const [hoveredLoc, setHoveredLoc] = useState<IGPSLocation>();

  const smallMobileSize = useMediaQuery(theme.breakpoints.down(430));
  const xsmallMobileSize = useMediaQuery(theme.breakpoints.down(360));
  const iphoneSEMobileSize = useMediaQuery(theme.breakpoints.down(330));

  const iosBrowser = useMemo(() => {
    const browser = detect();
    return browser?.name === "ios" || browser?.name === "crios";
  }, []);

  const geometries = useMemo(() => {
    if (geofences) {
      return convertGeofenceToGeometries(geofences);
    } else {
      return [];
    }
  }, [geofences]);

  useEffect(() => {
    const load = () => {
      dispatch(loadUserSettings());
    };

    const timer = setTimeout(load, 1000);
    return () => {
      clearTimeout(timer);
    };
  }, [dispatch]);

  useEffect(() => {
    if (map && maps && geometries && geometries.length > 0) {
      const objects = renderGeofences(map, maps, geometries, mobile, false);
      return () => {
        _.forEach(objects, (o) => o.setMap(null));
      };
    }
  }, [geometries, map, maps, mobile]);

  const noCluster = useMemo(() => {
    return clusters && !clusters.myzone && !clusters.zone;
  }, [clusters]);

  useEffect(() => {
    if (email && gpsWS && !isPublic) {
      const msg: GPSMessage = {
        action: "gpsPsnsMyDevices",
        email,
        sequenceId: new Date().getTime(),
      };
      gpsWS.send(JSON.stringify(msg));
    }
  }, [email, gpsWS, isPublic]);

  useEffect(() => {
    // 나의 대시캠 위치를 요청하였으나, 위치가 없는 경우, 현재 지도 상의 대시캠 요청
    if (
      initialRequest.current &&
      noCluster &&
      locations &&
      locations.length === 0 &&
      bounds &&
      mode !== "liveview"
    ) {
      if (email && gpsWsReadyState === WebSocket.OPEN) {
        if (!(bounds.south === bounds.north || bounds.west === bounds.east)) {
          initialRequest.current = false;
          const msg: GPSMessage = {
            action: "gpsZoneAllDevices",
            email,
            sw: `${bounds.south}_${bounds.west}`,
            ne: `${bounds.north}_${bounds.east}`,
            sequenceId: new Date().getTime(),
          };
          gpsWS?.send(JSON.stringify(msg));
        }
      }
    }
  }, [bounds, email, gpsWS, gpsWsReadyState, locations, mode, noCluster]);

  const getSVGIcon = useCallback(
    (
      loc: IGPSLocation,
      isEvent: boolean,
      isPublic: boolean,
      hideName: boolean,
      liveview: boolean,
      shadow: boolean
    ) => {
      const heading = loc.heading;
      let width = LocationWidth;
      let height = LocationHeight;
      let markerFill = LightColors.primary["7"].replace("#", "%23");
      let markerStroke = LightColors.primary["0"].replace("#", "%23");
      let arrowStroke = "%23E9E9EA";
      // if (loc.active === "off") {
      //   markerColor = LightColors.primary["3"].replace("#", "%23");
      // }

      let camSvg = `
      <rect x="38" y="2" width="22" height="14" rx="5" fill="%233C3C3C" stroke="white" stroke-width="2"/>
      <circle cx="45" cy="9" r="2" stroke="white" stroke-width="2"/>
      <circle cx="59" cy="4" r="3" fill="%23DF434D" stroke="white" stroke-width="2"/>
      `;
      // 10487 - 라이브뷰에서 대시캠 아이콘 표시
      if (isPublic || loc.active === "off") {
        camSvg = "";
      }

      let markerSvg = `
        <path d="M25 35C25 33.1814 25.7375 31.4372 27.0503 30.1513C28.363 28.8653 30.1435 28.1429 32 28.1429C33.8565 28.1429 35.637 28.8653 36.9497 30.1513C38.2625 31.4372 39 33.1814 39 35H25ZM32 27.2857C29.0994 27.2857 26.75 24.9843 26.75 22.1429C26.75 19.3014 29.0994 17 32 17C34.9006 17 37.25 19.3014 37.25 22.1429C37.25 24.9843 34.9006 27.2857 32 27.2857Z" fill="white"/>
      `;
      if (loc.mode === "1" && loc.active === "on") {
        markerSvg = `<path d="M32.6869 28.4248H28.7016V25.9111H32.6869C33.3028 25.9111 33.8041 25.8109 34.1908 25.6104C34.5776 25.4027 34.8604 25.1162 35.0395 24.751C35.2185 24.3857 35.308 23.974 35.308 23.5156C35.308 23.0501 35.2185 22.6169 35.0395 22.2158C34.8604 21.8148 34.5776 21.4925 34.1908 21.249C33.8041 21.0055 33.3028 20.8838 32.6869 20.8838H29.8188V34H26.5961V18.3594H32.6869C33.9115 18.3594 34.9607 18.5814 35.8344 19.0254C36.7153 19.4622 37.3884 20.0674 37.8539 20.8408C38.3194 21.6143 38.5522 22.4987 38.5522 23.4941C38.5522 24.5039 38.3194 25.3776 37.8539 26.1152C37.3884 26.8529 36.7153 27.4222 35.8344 27.8232C34.9607 28.2243 33.9115 28.4248 32.6869 28.4248Z" fill="white"/>`;
      } else if (isPublic) {
        markerSvg = ``;
      }

      if (loc.active === "off") {
        markerFill = "%2368686E";
      }
      if (isEvent) {
        markerFill = LightColors.secondary["11"].replace("#", "%23");
      }

      if (isPublic) {
        markerFill = LightColors.primary["0"].replace("#", "%23");
        if (loc.share_video === "on") {
          markerStroke = LightColors.secondary["11"].replace("#", "%23");
          arrowStroke = LightColors.secondary["11"].replace("#", "%23");
        }
      }

      let arrowYOffset = -7;
      height = 66;
      if (!hideName && loc.mode === "0" && loc.active === "on") {
        height = 77;
        arrowYOffset = 4;
      }

      let arrowMarkup = `<g style="transform:translate(0,${arrowYOffset}px)">
      <path d="M32.3838 72.2926C32.1995 72.5691 31.8005 72.5691 31.6162 72.2926L27.5822 66.2414C27.367 65.9186 27.6024 65.5 27.966 65.5L36.034 65.5C36.3976 65.5 36.633 65.9186 36.4178 66.2414L32.3838 72.2926Z" fill="${markerStroke}" stroke="${arrowStroke}"/>
      </g>`;

      let directionMarkup = ``;

      if (
        loc.mode === "0" &&
        showHeading &&
        heading !== undefined &&
        !isNaN(heading)
      ) {
        directionMarkup = `
        <g  style="transform-origin: 19px 40px; transform:translate(13px, -10px) rotate(${heading}deg);">

<path opacity="0.85" d="M19 40L9.00003 34.5L3.01998e-05 4.30733e-06L38 3.8147e-06L29 34.5L19 40Z" fill="url(%23paint0_linear_4980_10633)"/>
</g>
<defs>
<linearGradient id="paint0_linear_4980_10633" x1="19" y1="40" x2="19" y2="-6.66667" gradientUnits="userSpaceOnUse">
<stop offset="0.238239" stop-color="%230095E0"/>
<stop offset="0.833168" stop-color="%230095E0" stop-opacity="0"/>
</linearGradient>
</defs>

        `;
      }

      if (liveview) {
        arrowMarkup = "";
      }

      let background = `<path d="M37.2773 49.418C47.9932 47.0137 56 37.4419 56 26C56 12.7452 45.2549 2 32 2C18.7451 2 8 12.7452 8 26C8 37.5896 16.2148 47.2606 27.1396 49.5076C27.1494 49.5247 27.1602 49.5418 27.1719 49.5587L31.3555 55.7687C31.752 56.3571 32.6172 56.3571 33.0137 55.7687L37.1973 49.5587C37.2285 49.5128 37.2549 49.4658 37.2773 49.418Z" fill="${markerStroke}"/>`;
      if (isPublic) {
        background = `<g style="transform:translate(-2px, 0)">
        <path d="M39.3886 49.4957L39.1502 49.5487L39.0455 49.7694C39.0326 49.7965 39.0182 49.8219 39.0016 49.8461L39.0002 49.8481L34.6423 56.2631C34.4277 56.579 33.9568 56.579 33.7422 56.2631C33.7422 56.2631 33.7422 56.2631 33.7422 56.2631L29.3843 49.8481L29.3843 49.848L29.3804 49.8424C29.3764 49.8367 29.3733 49.8318 29.3709 49.8276L29.2585 49.6315L29.0371 49.5863C17.8824 47.3111 9.5 37.5196 9.5 25.7925C9.5 12.38 20.465 1.5 34 1.5C47.535 1.5 58.5 12.38 58.5 25.7925C58.5 37.3701 50.3299 47.0611 39.3886 49.4957Z" fill="${markerStroke}" stroke="${arrowStroke}"/>
        </g>`;
      }
      console.log(
        "getSVGIcon",
        loc,
        "isEvent",
        isEvent,
        "isPublic",
        isPublic,
        "hideName",
        hideName,
        "height",
        height,
        "width",
        width,
        "liveview",
        liveview,
        "markerSvg",
        markerSvg,
        "arrowMarkup",
        arrowMarkup
      );

      width = 64;
      let scale = 1;
      if (liveview) {
        scale = 0.56;
      }
      return {
        width,
        height,
        svg: `data:image/svg+xml;utf-8,
        <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" fill="none" data-psn="${
          loc.psn
        }" xmlns="http://www.w3.org/2000/svg">
        
        ${directionMarkup} 

        <g style="transform-origin:50% 50%;transform:scale(${scale})">
          ${arrowMarkup}
          
          ${background}


          <circle cx="32" cy="26" r="21" fill="${markerFill}"/>
          ${markerSvg}
          <g style="transform-origin:90% 40%;transform:scale(${
            liveview ? 1.54 : 1
          })">
            ${camSvg}  
          </g>
          
          <g clip-path="url(%23clip0_3821_95343)">
            <path d="M0 40H64V66H0V40Z" fill="white"/>
          </g>
          <defs>
            <clipPath id="clip0_3821_95343">
            </clipPath>
          </defs>
        </g>
        <filter id="filter0_ddddd_3821_96320" x="0" y="0" width="${width}" height="${height}" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
<feFlood flood-opacity="0" result="BackgroundImageFix"/>
<feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
<feOffset/>
<feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0"/>
<feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_3821_96320"/>
<feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
<feOffset/>
<feGaussianBlur stdDeviation="0.5"/>
<feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0"/>
<feBlend mode="normal" in2="effect1_dropShadow_3821_96320" result="effect2_dropShadow_3821_96320"/>
<feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
<feOffset dy="1"/>
<feGaussianBlur stdDeviation="0.5"/>
<feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.09 0"/>
<feBlend mode="normal" in2="effect2_dropShadow_3821_96320" result="effect3_dropShadow_3821_96320"/>
<feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
<feOffset dy="3"/>
<feGaussianBlur stdDeviation="1"/>
<feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0"/>
<feBlend mode="normal" in2="effect3_dropShadow_3821_96320" result="effect4_dropShadow_3821_96320"/>
<feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
<feOffset dy="6"/>
<feGaussianBlur stdDeviation="1"/>
<feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.01 0"/>
<feBlend mode="normal" in2="effect4_dropShadow_3821_96320" result="effect5_dropShadow_3821_96320"/>
<feBlend mode="normal" in="SourceGraphic" in2="effect5_dropShadow_3821_96320" result="shape"/>
</filter>
        </svg>`,
      };
    },
    [showHeading]
  );

  useEffect(() => {
    if (bounds) {
      onUpdateBounds?.(bounds);
    }
  }, [bounds, onUpdateBounds]);

  useEffect(() => {
    if (mode === "liveview" && center && map) {
      const { lat, lng } = center;
      if (lat && lng) {
        map.setCenter({ lat, lng });
      }
    } else if (center && map && prevBounds.current === undefined) {
      //메인 지도 화면에서 카메라 선택시 이동하는 로직
      prevBounds.current = map.getBounds();
      const { zoom, lat, lng } = center;
      if (lat && lng) {
        map.setCenter({ lat, lng });
      }
      if (zoom && map.getZoom() <= zoom) {
        map.setZoom(zoom);
      }
    } else if (map && center === undefined && prevBounds.current) {
      //메인 지도 화면에서 상세보기에서 빠져나오는 경우
      // map.fitBounds(prevBounds.current);
      prevBounds.current = undefined;
    }
  }, [center, map, mode]);

  useEffect(() => {
    function exitHandler() {
      if (
        !document.fullscreenElement &&
        //@ts-ignore
        !document.webkitIsFullScreen &&
        //@ts-ignore
        !document.mozFullScreen &&
        //@ts-ignore
        !document.msFullscreenElement
      ) {
        ///fire your event
        setfullscreen(false);
      }
    }
    document.addEventListener("fullscreenchange", exitHandler);
    document.addEventListener("webkitfullscreenchange", exitHandler);
    document.addEventListener("mozfullscreenchange", exitHandler);
    document.addEventListener("MSFullscreenChange", exitHandler);

    return () => {
      document.removeEventListener("fullscreenchange", exitHandler);
      document.removeEventListener("webkitfullscreenchange", exitHandler);
      document.removeEventListener("mozfullscreenchange", exitHandler);
      document.removeEventListener("MSFullscreenChange", exitHandler);
    };
  }, []);

  useEffect(() => {
    if (map && mode !== "liveview" && maps && activePublic && noCluster) {
      _.forEach(publicCams, (cam, indx) => {
        if (cam.active === "off") {
          return;
        }
        const loc = {
          lat: cam.lat,
          lng: cam.lng,
          heading: 0,
          psn: cam.psn,
          name: cam.dev_name,
          speed: cam.speed,
          share_video: cam.share_video,
          mode: cam.mode,
          active: cam.active,
          profile_img: cam.profile_img,
          share_dev_name: cam.share_dev_name,
          isPublic: true,
        };
        const current = publicMarkers.current;
        const found = _.find(current, (marker) => marker.psn === cam.psn);
        const labelText = getLocationLabel(
          loc,
          speedUnit ?? userSettings?.velocityUnit,
          loc.share_dev_name === "off"
        );
        if (found) {
          const { width, height, svg } = getSVGIcon(
            loc,
            false,
            true,
            loc.share_dev_name === "off",
            false,
            loc.psn === hoveredLoc?.psn
          );
          found.main.setPosition({ lat: cam.lat, lng: cam.lng });
          found.main.setIcon({
            anchor: new maps.Point(width / 2, height),
            size: new maps.Size(width, height),
            scaledSize: new maps.Size(width, height),
            url: svg,
          });

          const {
            width: labelWidth,
            height: labelHeight,
            single,
            svg: labelSvg,
          } = labelText;
          found.label?.setPosition({ lat: cam.lat, lng: cam.lng });
          const labelIcon = found.label?.getIcon();
          if (labelIcon) {
            labelIcon.anchor = new maps.Point(labelWidth / 2, single ? 26 : 36);
            labelIcon.size = new maps.Size(labelWidth, labelHeight);
            labelIcon.scaledSize = new maps.Size(labelWidth, labelHeight);
            labelIcon.url = labelSvg;
            found.label?.setIcon(labelIcon);
          }
          found.profile?.setPosition({ lat: cam.lat, lng: cam.lng });
          const profileIcon = found.profile?.getIcon();
          if (profileIcon) {
            profileIcon.anchor = new maps.Point(21, single ? 60.5 : 70.5);
            found.profile?.setIcon(profileIcon);
          }
          // found.profile?.setIcon({
          //   ...profileIcon,
          //   anchor: new maps.Point(21, single ? 60.5 : 70.5),
          // });
        } else {
          let label: any;
          let profile: any;
          if (labelText) {
            const {
              width: labelWidth,
              height: labelHeight,
              single,
              svg,
            } = labelText;
            label = new maps.Marker({
              position: { lat: cam.lat, lng: cam.lng },
              map: map,
              zIndex:
                cam.share_video === "on"
                  ? 50000 + indx * 3 + 2
                  : 10000 + indx * 3 + 2,
              icon: {
                origin: new maps.Point(0, 0),
                anchor: new maps.Point(labelWidth / 2, single ? 26 : 36),
                size: new maps.Size(labelWidth, labelHeight),
                scaledSize: new maps.Size(labelWidth, labelHeight),
                url: svg,
              },
            });

            profile = new maps.Marker({
              position: { lat: cam.lat, lng: cam.lng },
              map: map,
              zIndex:
                cam.share_video === "on"
                  ? 50000 + indx * 3 + 1
                  : 10000 + indx * 3 + 1,
              icon: {
                origin: new maps.Point(0, 0),
                size: new maps.Size(42, 42),
                scaledSize: new maps.Size(42, 42),
                anchor: new maps.Point(21, single ? 60.5 : 70.5),
                url: cam.profile_img,
              },
              title: "profile",
            });
          }
          const { width, height, svg } = getSVGIcon(
            loc,
            false,
            true,
            loc.share_dev_name === "off",
            false,
            loc.psn === hoveredLoc?.psn
          );
          const marker = new maps.Marker({
            //mantis - 8558 , 클릭한 카메라의 psn을 위한 추가
            psn: loc.psn,
            position: { lat: cam.lat, lng: cam.lng },
            map: map,
            zIndex:
              cam.share_video === "on" ? 50000 + indx * 3 : 10000 + indx * 3,
            icon: {
              anchor: new maps.Point(width / 2, height),
              size: new maps.Size(width, height),
              scaledSize: new maps.Size(width, height),
              url: svg,
            },
          });

          if (loc.active === "on") {
            if (loc.share_video === "on") {
              marker.addListener("click", () => {
                onLiveView?.(cam.psn, true);
              });
              profile?.addListener("click", () => {
                onLiveView?.(cam.psn, true);
              });
            } else {
              marker.addListener("click", () => {
                setPrivateCamMarker(marker);
              });
              profile?.addListener("click", () => {
                setPrivateCamMarker(marker);
              });
            }
          }
          profile?.addListener("mouseover", () => {
            console.log("mouseover");
            setHoveredLoc(loc);
          });

          profile?.addListener("mouseout", () => {
            console.log("mouseout");
            setHoveredLoc(undefined);
          });

          publicMarkers.current.push({
            main: marker,
            label,
            profile,
            psn: cam.psn,
          });
        }
      });
    } else {
      _.forEach(publicMarkers.current, (m) => {
        m.main.setMap(null);
        m.profile?.setMap(null);
        m.label?.setMap(null);
      });
      publicMarkers.current = [];
    }
  }, [
    activePublic,
    getSVGIcon,
    hoveredLoc?.psn,
    map,
    maps,
    mode,
    noCluster,
    onLiveView,
    publicCams,
    speedUnit,
    userSettings,
  ]);

  useEffect(() => {
    if (map && maps) {
      const initZoom = mode === "liveview" ? 15 : 17;
      const idleListener = map.addListener("idle", () => {
        const newBounds = map.getBounds().toJSON() as ILatLngBounds;
        if (!_.isEqual(bounds, newBounds) && !isNaN(newBounds.south)) {
          setBounds(newBounds);

          // console.log(
          //   "CloudNative3",
          //   "LocationMap",
          //   "newBounds",
          //   newBounds,
          //   "s/n",
          //   newBounds.south === newBounds.north,
          //   "w/e",
          //   newBounds.west === newBounds.east
          // );

          if (email && gpsWsReadyState === WebSocket.OPEN) {
            if (mode !== "liveview" && !location) {
              if (
                !(
                  newBounds.south === newBounds.north ||
                  newBounds.west === newBounds.east
                )
              ) {
                const msg: GPSMessage = {
                  action: "gpsZoneAllDevices",
                  email,
                  sw: `${newBounds.south}_${newBounds.west}`,
                  ne: `${newBounds.north}_${newBounds.east}`,
                  sequenceId: new Date().getTime(),
                };
                gpsWS?.send(JSON.stringify(msg));
              }
            }
          }
        }
      });
      const zoomListener = map.addListener("zoom_changed", () => {
        if (map.initialZoom === true) {
          const boundsListener = map.addListener("bounds_changed", () => {
            console.log("bounds_changed");
            if (map.getZoom() > initZoom) {
              // Change max/min zoom here
              map.setZoom(initZoom);
              map.initialZoom = false;
            }
            //@ts-ignore
            maps.event.removeListener(boundsListener);
          });
        }
      });

      const clickListener = map.addListener("click", () => {
        onSetInfoWindow?.(undefined);
        setPrivateCamMarker(undefined);
      });
      return () => {
        //@ts-ignore
        maps.event.removeListener(zoomListener);
        maps.event.removeListener(clickListener);
        maps.event.removeListener(idleListener);
      };
    }
  }, [
    onSetInfoWindow,
    map,
    maps,
    bounds,
    mode,
    email,
    gpsWsReadyState,
    gpsWS,
    location,
  ]);

  useEffect(() => {
    if (!location) {
      locationMarker.current?.main?.setMap(null);
      locationMarker.current?.profile?.setMap(null);
      locationMarker.current?.label?.setMap(null);
      locationMarker.current = undefined;
      prevLocation.current = undefined;
    }
  }, [location]);

  useEffect(() => {
    console.log("LocationMap", location, trackMarker, noMarker);
    if (map && maps && trackMarker && location) {
      if (prevLocation.current) {
        map.setCenter({ lat: location.lat, lng: location.lng });
      } else {
        map.initialZoom = true;

        const { lat, lng } = location;
        map.setCenter({ lat, lng });
        map.setZoom(21);
      }

      const labelText = getLocationLabel(
        location,
        speedUnit ?? userSettings?.velocityUnit,
        true
      );
      if (!noMarker) {
        if (locationMarker.current) {
          const { width, height, svg } = getSVGIcon(
            location,
            false,
            false,
            true,
            false,
            location.psn === hoveredLoc?.psn
          );
          locationMarker.current?.main?.setPosition(location);
          locationMarker.current?.main?.setIcon({
            anchor: new maps.Point(width / 2, height),
            size: new maps.Size(width, height),
            scaledSize: new maps.Size(width, height),
            url: svg,
          });
          if (labelText) {
            const {
              width: labelWidth,
              height: labelHeight,
              svg,
              single,
            } = labelText;

            locationMarker.current?.label?.setPosition(location);
            locationMarker.current?.label?.setIcon({
              origin: new maps.Point(0, 0),
              anchor: new maps.Point(labelWidth / 2, single ? 26 : 36),
              size: new maps.Size(labelWidth, labelHeight),
              scaledSize: new maps.Size(labelWidth, labelHeight),
              url: svg,
            });
          }
        } else {
          let label: any;
          if (labelText) {
            const {
              width: labelWidth,
              height: labelHeight,
              svg,
              single,
            } = labelText;
            label = new maps.Marker({
              position: location,
              map: map,
              zIndex: 2,
              icon: {
                origin: new maps.Point(0, 0),
                anchor: new maps.Point(labelWidth / 2, single ? 26 : 36),
                size: new maps.Size(labelWidth, labelHeight),
                scaledSize: new maps.Size(labelWidth, labelHeight),
                url: svg,
              },
            });
          }
          const { width, height, svg } = getSVGIcon(
            location,
            false,
            false,
            true,
            false,
            location.psn === hoveredLoc?.psn
          );
          const main = new maps.Marker({
            position: location,
            map: map,
            icon: {
              anchor: new maps.Point(width / 2, height),
              size: new maps.Size(width, height),
              scaledSize: new maps.Size(width, height),
              url: svg,
            },
          });
          locationMarker.current = { main, label };
        }
      }
      prevLocation.current = location;
    }
  }, [
    trackMarker,
    location,
    map,
    maps,
    getSVGIcon,
    noMarker,
    userSettings,
    hoveredLoc?.psn,
    speedUnit,
  ]);

  useEffect(() => {
    if (clusters && map && mode !== "liveview" && publicIcon) {
      let minLat = 999,
        minLng = 999,
        maxLat = -999,
        maxLng = -999;
      let ms: {} = {};
      if (activePublic) {
        for (let indx in clusters.zone ?? []) {
          const zone = clusters.zone?.[indx];
          if (zone) {
            const lat = parseFloat(zone.lat);
            const lng = parseFloat(zone.lng);
            const cnt = parseInt(zone.count);
            minLat = Math.min(minLat, lat);
            minLng = Math.min(minLng, lng);
            maxLat = Math.max(maxLat, lat);
            maxLng = Math.max(maxLng, lng);
            ms = {
              ...ms,
              [`zone-${indx}`]: (
                <MarkerCluster
                  key={`zone-${indx}`}
                  lat={lat}
                  lng={lng}
                  size={cnt < 100 ? "small" : "medium"}
                  variant="outlined"
                  label={zone.count}
                  onClick={() => {
                    map.setCenter({ lat, lng });
                    map.setZoom(map.getZoom() + 1);
                  }}
                />
              ),
            };
          }
        }
      }
      for (let indx in clusters.myzone ?? []) {
        const zone = clusters.myzone?.[indx];
        if (zone) {
          const lat = parseFloat(zone.lat);
          const lng = parseFloat(zone.lng);
          const cnt = parseInt(zone.count);
          minLat = Math.min(minLat, lat);
          minLng = Math.min(minLng, lng);
          maxLat = Math.max(maxLat, lat);
          maxLng = Math.max(maxLng, lng);
          ms = {
            ...ms,
            [`myzone-${indx}`]: (
              <MarkerCluster
                key={`myzone-${indx}`}
                lat={lat}
                lng={lng}
                size={cnt < 100 ? "small" : "medium"}
                variant="default"
                label={zone.count}
                style={{ zIndex: 10 }}
                onClick={() => {
                  map.setCenter({ lat, lng });
                  map.setZoom(map.getZoom() + 1);
                }}
              />
            ),
          };
        }
      }
      setMarkers(ms);
    } else {
      setMarkers({});
    }
  }, [map, clusters, publicIcon, activePublic, mode]);

  useEffect(() => {
    return () => {
      _.forEach(gMarkers.current, (c) => {
        c.main.setMap(null);
        c.profile?.setMap(null);
        c.label?.setMap(null);
      });
      gMarkers.current = [];
      _.forEach(publicMarkers.current, (m) => {
        m.main.setMap(null);
        m.profile?.setMap(null);
        m.label?.setMap(null);
      });
      publicMarkers.current = [];
    };
  }, []);

  useEffect(() => {
    if (!activePublic) {
      _.forEach(publicMarkers.current, (m) => {
        m.main.setMap(null);
        m.profile?.setMap(null);
        m.label?.setMap(null);
      });
      publicMarkers.current = [];
    }
  }, [activePublic]);

  useEffect(() => {
    if (webPushEvent && maps) {
      const current = gMarkers.current;
      const found = _.find(current, (loc) => loc.psn === webPushEvent.psn);
      if (found) {
        found.event = webPushEvent;
        found.main.setClickable(true);
        found.main.addListener("click", () => {
          onLiveView?.(found.psn, false);
          //팝업 열어주는 코드
          // setSelectedMarker(found);
        });
        const timerId = setInterval(() => {
          const { width, height, svg } = getSVGIcon(
            found.loc,
            found.isEvent,
            false,
            false,
            false,

            found.loc.psn === hoveredLoc?.psn
          );
          // 10371 - 마커 위치 문제 수정 (mckim)
          found.main.setIcon({
            anchor: new maps.Point(width / 2, height),
            size: new maps.Size(width, height),
            scaledSize: new maps.Size(width, height),
            url: svg,
          });
          found.isEvent = !found.isEvent;
        }, 500);
        return () => {
          found.isEvent = false;
          clearInterval(timerId);
        };
        // setSelectedMarker(found);
      }
    } else {
      // setSelectedMarker(undefined);
    }
  }, [getSVGIcon, hoveredLoc?.psn, maps, onLiveView, webPushEvent]);

  useEffect(() => {
    //라이브뷰 랜더링
    if (locations && map && maps && (noCluster || mode === "liveview")) {
      const filteredLocations = _.filter(
        locations,
        (loc) => loc.agree_gps === "on" || loc.isPublic
      );
      const current = gMarkers.current;
      const psns = _.map(filteredLocations, (loc) => loc.psn);
      const filteredPsns = _.map(filteredCams, (cam) => cam.psn);
      const removed = _.filter(current, (loc) => {
        const l = _.find(filteredLocations, (ll) => ll.psn === loc.psn);
        if (l) {
          return (
            !_.includes(psns, loc.psn) ||
            (filteredCams && !_.includes(filteredPsns, loc.psn)) ||
            l.lat === 0 ||
            l.lng === 0
          );
        }
        return true;
      });
      const updated = _.filter(current, (loc) => {
        const l = _.find(filteredLocations, (ll) => ll.psn === loc.psn);
        if (l) {
          return (
            _.includes(psns, loc.psn) &&
            (!filteredCams || _.includes(filteredPsns, loc.psn)) &&
            !(l.lat === 0 || l.lng === 0)
          );
        }
        return false;
      });

      console.log(
        "locations",
        psns,
        locations,
        filteredLocations,
        removed,
        updated
      );

      _.forEach(removed, (r) => {
        r.main.setMap(null);
        r.profile?.setMap(null);
        r.label?.setMap(null);
      });

      let needToUpdate = false;
      const ms = _.chain(filteredLocations)
        .filter(
          (loc) =>
            (filteredCams ? _.includes(filteredPsns, loc.psn) : true) &&
            !(loc.lat === 0 || loc.lng === 0)
        )
        .map((loc, indx) => {
          const found = _.find(updated, (c) => c.psn === loc.psn);
          const isPublic = !!_.find(publicCams, (c) => c.psn === loc.psn);
          const labelText = getLocationLabel(
            loc,
            speedUnit ?? userSettings?.velocityUnit,
            mode === "liveview"
          );

          if (found) {
            const { width, height, svg } = getSVGIcon(
              loc,
              found.isEvent,
              isPublic,
              mode === "liveview",
              mode === "liveview",
              mode !== "liveview" && loc.psn === hoveredLoc?.psn
            );
            found.main?.setPosition({ lat: loc.lat, lng: loc.lng });
            found.main?.setIcon({
              anchor: new maps.Point(width / 2, height),
              size: new maps.Size(width, height),
              scaledSize: new maps.Size(width, height),
              url: svg,
            });
            // mantis - 10822, 공유캠에는 P라벨 나오고 my cam에는 나오지 않도록 && !isPublic 조건 추가(Leehj)
            if (mode === "liveview" && loc.mode === "1" && !isPublic) {
              found.label?.setMap(null);
              found.label = undefined;
            } else if (labelText) {
              const {
                width: labelWidth,
                height: labelHeight,
                single,
                svg: lineSvg,
              } = labelText;
              if (found.label) {
                found.label?.setPosition({ lat: loc.lat, lng: loc.lng });
                found.label?.setIcon({
                  origin: new maps.Point(0, 0),
                  // mantis - 10204, 라이브 뷰 공유된 카메라 labelText위치수정. (Leehj)
                  // 10487 높이 수정 (mckim)
                  anchor: new maps.Point(
                    labelWidth / 2,
                    isPublic ? 32 : single ? 26 : 36
                  ),
                  size: new maps.Size(labelWidth, labelHeight),
                  scaledSize: new maps.Size(labelWidth, labelHeight),
                  url: lineSvg,
                });
              } else {
                found.label = new maps.Marker({
                  position: { lat: loc.lat, lng: loc.lng },
                  map: map,
                  zIndex: 60000 + indx * 3 + 2,
                  icon: {
                    origin: new maps.Point(0, 0),
                    // 10487 높이 수정 (mckim)
                    anchor: new maps.Point(
                      labelWidth / 2,
                      isPublic ? 32 : single ? 26 : 36
                    ),
                    size: new maps.Size(labelWidth, labelHeight),
                    scaledSize: new maps.Size(labelWidth, labelHeight),
                    url: lineSvg,
                  },
                });
              }
            }
            found.profile?.setPosition({ lat: loc.lat, lng: loc.lng });
            // if (!isEvent) {
            //   found.setClickable(false);
            //   // found.setAnimation(null);
            //   maps.event.clearListeners(found, "click");
            //   found.event = undefined;
            // }
            found.loc = loc;
            return found;
          } else {
            needToUpdate = true;
            let label: any;

            const { width, height, svg } = getSVGIcon(
              loc,
              false,
              isPublic,
              mode === "liveview",
              mode === "liveview",
              mode !== "liveview" && loc.psn === hoveredLoc?.psn
            );
            // 10487 - 파킹모드 라이브뷰에서는 하단 라벨 표시 안함 (mckim)
            if (
              labelText &&
              !(mode === "liveview" && loc.mode === "1" && !isPublic)
            ) {
              const {
                width: labelWidth,
                height: labelHeight,
                single,
                svg: labelSvg,
              } = labelText;
              label = new maps.Marker({
                position: { lat: loc.lat, lng: loc.lng },
                map: map,
                zIndex: 60000 + indx * 3 + 2,
                icon: {
                  origin: new maps.Point(0, 0),
                  // 10487 높이 수정 (mckim)
                  anchor: new maps.Point(
                    labelWidth / 2,
                    isPublic ? 32 : single ? 26 : 36
                  ),
                  size: new maps.Size(labelWidth, labelHeight),
                  scaledSize: new maps.Size(labelWidth, labelHeight),
                  url: labelSvg,
                },
              });
            }

            const marker = new maps.Marker({
              position: { lat: loc.lat, lng: loc.lng },
              map: map,
              zIndex: 60000 + indx * 3,
              icon: {
                origin: new maps.Point(0, 0),
                anchor: new maps.Point(width / 2, height),
                size: new maps.Size(width, height),
                scaledSize: new maps.Size(width, height),
                url: svg,
              },
            });

            const profile = isPublic
              ? new maps.Marker({
                  position: { lat: loc.lat, lng: loc.lng },
                  map: map,
                  zIndex: 60000 + indx * 3 + 1,
                  icon: {
                    origin: new maps.Point(0, 0),
                    size: new maps.Size(42, 42),
                    // mantis - 10204, 라이브 뷰 공유된 카메라 사이즈 24*24로 수정 및 위치수정. (Leehj)
                    scaledSize: new maps.Size(24, 24),
                    anchor: new maps.Point(12, 48.5),
                    // scaledSize: new maps.Size(42, 42),
                    // anchor: new maps.Point(21, 60.5),
                    url: loc.profile_img,
                  },
                  title: "profile",
                })
              : undefined;

            if (loc.active === "on") {
              marker.addListener("click", () => {
                onLiveView?.(loc.psn, false);
              });
            }
            return {
              main: marker,
              profile,
              label,
              loc,
              psn: loc.psn,
              model: loc.model,
            };
          }
        })
        .value();
      if (
        needToUpdate &&
        (!disableAutoZoom ||
          (mode === "liveview" && filteredLocations.length === 1) ||
          !initMap)
      ) {
        console.log("autoZoom!!!");
        let minLat = 999,
          minLng = 999,
          maxLat = -999,
          maxLng = -999;
        _.forEach(ms, (m) => {
          const { lat, lng } = m.loc;
          minLat = Math.min(lat, minLat);
          minLng = Math.min(lng, minLng);
          maxLat = Math.max(lat, maxLat);
          maxLng = Math.max(lng, maxLng);
        });

        map.initialZoom = true;
        map.fitBounds({
          east: maxLng,
          north: maxLat,
          west: minLng,
          south: minLat,
        });

        setInitMap(true);
      }
      gMarkers.current = ms;
    } else {
      const current = gMarkers.current;
      _.forEach(current, (m) => {
        m.main?.setMap(null);
        m.profile?.setMap(null);
        m.label?.setMap(null);
      });
      gMarkers.current = [];
    }
  }, [
    disableAutoZoom,
    filteredCams,
    getSVGIcon,
    hoveredLoc?.psn,
    initMap,
    locations,
    map,
    maps,
    mode,
    noCluster,
    onLiveView,
    publicCams,
    speedUnit,
    userSettings,
    webPushEvent,
  ]);

  const browserName = useMemo(() => {
    return detect()?.name;
  }, []);

  const handleFullscreen = useCallback(() => {
    if (iosBrowser) {
      setfullscreen((f) => !f);
    } else {
      let elementToSendFullscreen: HTMLDivElement | null =
        mapRef?.current ?? mapDivRef.current;
      if (elementToSendFullscreen) {
        toggleFullscreen(elementToSendFullscreen);
        //@ts-ignore
        setfullscreen(elementToSendFullscreen.fullscreen);
        //@ts-ignore
        dispatch(setFullscreenTheme(elementToSendFullscreen.fullscreen));

        if (app) {
          //@ts-ignore
          if (elementToSendFullscreen.fullscreen) {
            if (
              browserName === "ios-webview" &&
              //@ts-ignore
              webkit.messageHandlers.enableRotate
            ) {
              //@ts-ignore
              webkit.messageHandlers.enableRotate.postMessage({});
            }
            if (
              browserName === "chromium-webview" &&
              //@ts-ignore
              window.Webviewer?.enableRotate
            ) {
              //@ts-ignore
              window.Webviewer?.enableRotate?.();
            }
          } else {
            if (
              browserName === "ios-webview" &&
              //@ts-ignore
              webkit.messageHandlers.disableRotate
            ) {
              //@ts-ignore
              webkit.messageHandlers.disableRotate.postMessage({});
            }
            if (
              browserName === "chromium-webview" &&
              //@ts-ignore
              window.Webviewer?.disableRotate
            ) {
              //@ts-ignore
              window.Webviewer?.disableRotate?.();
            }
          }
        }
      }
    }
  }, [app, browserName, dispatch, iosBrowser, mapRef]);

  const mapTopRightMenuMarkup = useMemo(() => {
    return (
      <div className={classes.topControlPane}>
        <TopRightControl
          round
          ref={anchorRef}
          publicRef={publicRef}
          onFullscreen={fullscreenIcon ? handleFullscreen : undefined}
          onLayers={() => setOpenLayer((o) => !o)}
          onDashcamPublic={
            publicIcon
              ? () => {
                  setOpenCamFilter((o) => !o);
                }
              : undefined
          }
          fullscreen={fullscreen}
          activePublic={activePublic}
        />
        <Menu
          open={openCamFilter}
          onClickAway={() => setOpenCamFilter(false)}
          anchorEl={publicRef.current}
          // mantis - 10778, 모바일 피그마 적용 (Leehj)
          placement={smallMobileSize ? "bottom" : "bottom-end"}
          modifiers={{
            offset: {
              enabled: true,
              // mantis - 10778, 모바일 피그마 적용 및 잘려보이는 것 방지하기 위한 위치 조정 (Leehj)
              offset: iphoneSEMobileSize
                ? "-40px, 4px"
                : xsmallMobileSize
                ? "-50px, 4px"
                : smallMobileSize
                ? "-65px, 4px"
                : "0, 4px",
            },
            preventOverflow: {
              enabled: false,
            },
          }}
          classes={{
            root: classes.camFilterRoot,
          }}
        >
          <div className={classes.camFilterDiv}>
            <div className={classes.camFilterTitle}>
              <Typography
                category="Default"
                variant="SmallBold"
                htmlColor={LightColors.primary["7"]}
              >
                {t("Public")}
              </Typography>
            </div>
            <div>
              <div className={classes.camFilterListItem}>
                <Typography category="Default" variant="Body">
                  {t("BlackVue Live Camera")}
                </Typography>

                <Switch
                  checked={activePublic}
                  onChange={(_e, checked) => {
                    setActivePublic(checked);
                  }}
                />
              </div>
            </div>
          </div>
        </Menu>
        {!mobile && (
          <Menu
            open={openLayer}
            onClickAway={() => setOpenLayer(false)}
            anchorEl={anchorRef.current}
            placement="bottom-end"
            modifiers={{
              offset: {
                enabled: true,
                offset: "0, 4px",
              },
              preventOverflow: {
                enabled: false,
              },
            }}
          >
            <WebMenuItem
              className={clsx({
                [classes.tnpDiv]: mapMode === "satellite",
              })}
              startIcon={
                mapMode === "map" && <CheckIcon className={classes.appIcon} />
              }
              onClick={() => {
                setMapMode("map");
                map?.setMapTypeId("roadmap");
                setOpenLayer(false);
              }}
            >
              {t("Map")}
            </WebMenuItem>
            <WebMenuItem
              className={clsx({
                [classes.tnpDiv]: mapMode === "map",
              })}
              startIcon={
                mapMode === "satellite" && (
                  <CheckIcon className={classes.appIcon} />
                )
              }
              onClick={() => {
                setMapMode("satellite");
                map?.setMapTypeId("satellite");
                setOpenLayer(false);
              }}
            >
              {t("Satellite")}
            </WebMenuItem>
          </Menu>
        )}
        {mobile && (
          <MobileMenu
            open={openLayer}
            onClose={() => setOpenLayer(false)}
            container={anchorRef.current}
            classes={{ root: classes.textTransform }}
          >
            <WebMenuItem
              className={classes.mobileMemuItem}
              endIcon={
                mapMode === "map" && <CheckIcon className={classes.appIcon} />
              }
              onClick={() => {
                setMapMode("map");
                map?.setMapTypeId("roadmap");
                setOpenLayer(false);
              }}
            >
              {t("Map")}
            </WebMenuItem>
            <WebMenuItem
              className={classes.mobileMemuItem}
              endIcon={
                mapMode === "satellite" && (
                  <CheckIcon className={classes.appIcon} />
                )
              }
              onClick={() => {
                setMapMode("satellite");
                map?.setMapTypeId("satellite");
                setOpenLayer(false);
              }}
            >
              {t("Satellite")}
            </WebMenuItem>
          </MobileMenu>
        )}
      </div>
    );
  }, [
    classes.topControlPane,
    classes.camFilterRoot,
    classes.camFilterDiv,
    classes.camFilterTitle,
    classes.camFilterListItem,
    classes.tnpDiv,
    classes.appIcon,
    classes.textTransform,
    classes.mobileMemuItem,
    fullscreenIcon,
    handleFullscreen,
    publicIcon,
    fullscreen,
    activePublic,
    openCamFilter,
    smallMobileSize,
    iphoneSEMobileSize,
    xsmallMobileSize,
    t,
    mobile,
    openLayer,
    mapMode,
    map,
  ]);

  const zoomPanelMarkup = useMemo(() => {
    return (
      // mantis - 9061, touch이벤트 사용하여 모바일의경우 touch한 경우에만 아이콘 색 변경되도록 수정.(Leehj)
      <div
        className={classes.zoomControlDiv}
        onTouchStart={() => setTouch(true)}
        onTouchEnd={() => setTouch(false)}
      >
        {showLocation && (
          <Tooltip
            placement="left"
            disableTouchListener={mobile}
            title={t("My location") ?? "My location"}
            PopperProps={{
              disablePortal: true,
              modifiers: {
                preventOverflow: { enabled: false },
                flip: { enabled: false },
              },
            }}
          >
            <Fab
              size="small"
              variant="rounded"
              className={clsx(classes.zoomControlBtn, classes.gpsFixBtn, {
                // mantis - 9061, touch이벤트 사용하여 모바일의경우 touch한 경우에만 아이콘 색 변경되도록 수정.(Leehj)
                [classes.gpsFixBtnTouch]: touch,
                [classes.noHover]: mobile,
              })}
              onClick={() => {
                if (navigator.geolocation) {
                  navigator.geolocation.getCurrentPosition(
                    (position: GeolocationPosition) => {
                      const pos = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                      };
                      map.setCenter(pos);
                    },
                    () => {}
                  );
                }
              }}
            >
              <GpsFixedIcon fontSize="small" />
            </Fab>
          </Tooltip>
        )}
        {!mobile && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              borderRadius: 8,
              boxShadow:
                "0px 0px 1px rgba(0, 0, 0, 0.14), 0px 1px 1px rgba(0, 0, 0, 0.12), 0px 0px 3px rgba(0, 0, 0, 0.2)",
            }}
          >
            <Tooltip
              title={t("Zoom in") ?? "Zoom in"}
              PopperProps={{
                disablePortal: true,
                modifiers: {
                  preventOverflow: { enabled: false },
                  flip: { enabled: false },
                },
                placement: theme.direction === "rtl" ? "right" : "left",
              }}
            >
              <Fab
                size="small"
                variant="rounded"
                className={clsx(classes.zoomControlBtn, classes.zoomInBtn)}
                onClick={() => {
                  map.setZoom(map.getZoom() + 1);
                }}
              >
                <AddIcon fontSize="small" />
              </Fab>
            </Tooltip>
            <Tooltip
              title={t("Zoom out") ?? "Zoom out"}
              PopperProps={{
                disablePortal: true,
                modifiers: {
                  preventOverflow: { enabled: false },
                  flip: { enabled: false },
                },
                placement: theme.direction === "rtl" ? "right" : "left",
              }}
            >
              <Fab
                size="small"
                variant="rounded"
                className={clsx(classes.zoomControlBtn, classes.zoomOutBtn)}
                onClick={() => {
                  map.setZoom(map.getZoom() - 1);
                }}
              >
                <RemoveIcon fontSize="small" />
              </Fab>
            </Tooltip>
          </div>
        )}
      </div>
    );
  }, [
    classes.zoomControlDiv,
    classes.zoomControlBtn,
    classes.gpsFixBtn,
    classes.gpsFixBtnTouch,
    classes.noHover,
    classes.zoomInBtn,
    classes.zoomOutBtn,
    showLocation,
    mobile,
    t,
    touch,
    theme.direction,
    map,
  ]);

  const infoWindow = useMemo(() => {
    if (selectedMarker && selectedMarker.event) {
      const latLng = selectedMarker.getPosition();
      console.log("renderInfoWindow", latLng, selectedMarker.event);
      return (
        <div
          //@ts-ignore
          lat={latLng.lat()}
          //@ts-ignore
          lng={latLng.lng()}
          className={clsx(
            classes.infoWindowDiv,
            classes.infoWindowCenter,
            classes.parkingDiv
          )}
        >
          <EventCard
            event={selectedMarker.event}
            fileType="Event"
            model={selectedMarker.model}
            onClose={() => setSelectedMarker(undefined)}
          />
        </div>
      );
    }
    return undefined;
  }, [
    classes.infoWindowCenter,
    classes.infoWindowDiv,
    classes.parkingDiv,
    selectedMarker,
  ]);

  const privateCamWindow = useMemo(() => {
    if (privateCamMarker) {
      const found = _.find(
        publicCams,
        (cam) => cam.psn === privateCamMarker.psn
      );
      console.log("found", found);
      if (found) {
        return (
          <div
            //@ts-ignore
            lat={found.lat}
            //@ts-ignore
            lng={found.lng}
            className={clsx(
              classes.parkingDiv,
              classes.privateCamWindowCenter,
              classes.privateCamDiv
            )}
          >
            {t("This camera is publicly_")}
          </div>
        );
      }
    }
    return undefined;
  }, [
    classes.parkingDiv,
    classes.privateCamDiv,
    classes.privateCamWindowCenter,
    privateCamMarker,
    publicCams,
    t,
  ]);

  return (
    <div
      ref={fullscreen ? mapRef ?? mapDivRef : mapDivRef}
      style={{ width: "100%", height: "100%" }}
      className={clsx({
        [classes.rootFullscreen]: fullscreen,
      })}
    >
      <div style={{ width: "100%", height: "100%", position: "relative" }}>
        <GoogleMapReact
          style={{
            width: "100%",
            height: "100%",
            //@ts-ignore
            position: "relative",
            touchAction: "none",
          }}
          bootstrapURLKeys={{
            key: GOOGLE_API_KEY,
            libraries: ["geometry", "drawing"],
          }}
          defaultCenter={INITIAL_MAP_LOCATION}
          defaultZoom={4}
          //지도에서 bus stop 클릭되는 문제 수정 (8356)
          options={{ disableDefaultUI: true, clickableIcons: false }}
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={({ maps, map, ref }) => {
            setMap(map);
            setMaps(maps);
            onGoogleApiLoaded?.({ maps, map, ref });
          }}
          // onChange={(value) => console.log(value)}
        >
          {_.values(markers)}
          {children}
          {selectedMarker && infoWindow}
          {privateCamMarker && privateCamWindow}
        </GoogleMapReact>
        {mapTopRightMenuMarkup}
        {zoomPanelMarkup}
      </div>
    </div>
  );
};
